import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import axios from "axios";
import * as XLSX from "xlsx";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

function FuelLogUpload() {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // ✅ Download Excel Template
  const downloadTemplate = () => {
    const headers = [
      "invoiceNo",
      "invoiceDate",
      "fuellingStation",
      "fuelStationCity",
      "vehicleNo",
      "driverName",
      "driverId",
      "fuelOdometerReading",
      "fuelClosingMeterReading",
      "fuelQty",
      "ratePerLitre",
      "amount",
      "mileage",
      "fuelType",
      "paymentMode",
      "vendorCode",
      "billStatus",
      "costCenter",
      "projectCode",
      "remarks",
      "lastOdometerReading",
      "kmTravelledSinceLastFill",
      "fuelEfficiency",
      "costPerKm",
      "createdBy",
    ];

    const sampleRow = {
      invoiceNo: "INV-2025-0012",
      invoiceDate: "2025-11-08T10:45:00",
      fuellingStation: "HP Fuel Station - Sector 18",
      fuelStationCity: "Noida",
      vehicleNo: "UP16AB1234",
      driverName: "Ramesh Kumar",
      driverId: "DRV-0045",
      fuelOdometerReading: 45210.5,
      fuelClosingMeterReading: 45280.2,
      fuelQty: 35.6,
      ratePerLitre: 97.5,
      amount: 3477.0,
      mileage: 14.5,
      fuelType: "Diesel",
      paymentMode: "UPI",
      vendorCode: "VND-HP001",
      billStatus: "Pending",
      costCenter: "CC-TRUCKS-NORTH",
      projectCode: "PRJ-DELHI-NCR",
      remarks: "Refueled before trip to Gurgaon",
      lastOdometerReading: 45010.0,
      kmTravelledSinceLastFill: 270.2,
      fuelEfficiency: 13.8,
      costPerKm: 12.86,
      createdBy: "admin_user",
    };

    const worksheet = XLSX.utils.json_to_sheet([sampleRow], { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Fuel Log Template");
    XLSX.writeFile(workbook, "FuelLogUpload_Template.xlsx");
  };

  // ✅ File change handler
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setPreviewData([]);
    setErrorMessage("");

    if (!selectedFile) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const workbook = XLSX.read(bstr, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      // Convert sheet to JSON directly (key:value mapping)
      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!jsonData || jsonData.length === 0) {
        setErrorMessage("Excel file has no valid data.");
        openErrorSB();
        return;
      }

      console.log("📄 Parsed Data:", jsonData);
      setPreviewData(jsonData);
    };
    reader.readAsBinaryString(selectedFile);
  };

  // ✅ Upload handler
  const handleUpload = async () => {
    if (!file) {
      setErrorMessage("Please select a file to upload");
      openErrorSB();
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.post(
        "https://v3.dhundhoo.com/fuel/log/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-dhundhoo-session": token,
          },
        }
      );

      console.log("✅ Upload successful!", response.data);
      openSuccessSB();
      setFile(null);
      setPreviewData([]);
    } catch (error) {
      console.error("❌ Upload failed:", error);
      setErrorMessage("Failed to upload file. Please check format and try again.");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setPreviewData([]);
    setErrorMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ height:'85px' }}>
              <MDBox p={3} pb={0}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={5}>
                    <TextField
                      type="file"
                      accept=".xlsx, .xls"
                      fullWidth
                      size="small"
                      onChange={handleFileChange}
                    />
                  </Grid>

                  <Grid item xs={6} sm={2} md={2}>
                    <MDButton
                      fullWidth
                      variant="gradient"
                      color="warning"
                      size="small"
                      onClick={handleUpload}
                      disabled={loading || previewData.length === 0}
                    >
                      {loading ? <CircularProgress size={20} color="inherit" /> : "UPLOAD"}
                    </MDButton>
                  </Grid>

                  <Grid item xs={6} sm={2} md={2}>
                    <MDButton
                      fullWidth
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleClear}
                      disabled={loading}
                    >
                      CLEAR
                    </MDButton>
                  </Grid>

                  <Grid item xs={6} sm={2} md={2}>
                    <MDButton
                      fullWidth
                      variant="gradient"
                      color="secondary"
                      size="small"
                      onClick={downloadTemplate}
                    >
                      DOWNLOAD TEMPLATE
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>

              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              {/* ✅ Preview Table */}
              {previewData.length > 0 && (
                <MDBox p={3}>
                  <Alert severity="info">
                    Previewing {previewData.length} row(s) from selected file
                  </Alert>
                  <TableContainer
                    component={Paper}
                    sx={{
                      maxHeight: 400,
                      overflowX: "auto",
                      mt: 2,
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {Object.keys(previewData[0]).map((key) => (
                            <TableCell
                              key={key}
                              sx={{
                                fontWeight: "bold",
                                background: "#f5f5f5",
                                whiteSpace: "nowrap",
                                minWidth: 150,
                              }}
                            >
                              {key}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.map((row, idx) => (
                          <TableRow key={idx}>
                            {Object.keys(previewData[0]).map((key) => (
                              <TableCell
                                key={key}
                                sx={{ whiteSpace: "nowrap", minWidth: 150 }}
                              >
                                {String(row[key] ?? "")}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color="success"
        icon="check"
        title="Fuel Log Upload"
        content="File uploaded successfully."
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />

      <MDSnackbar
        color="error"
        icon="warning"
        title="Fuel Log Upload"
        content={errorMessage || "Upload failed"}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default FuelLogUpload;
